import { useState, useEffect } from 'react';
import { getProjectVideoByName } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

interface NewProjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewProjectsModal = ({ isOpen, onClose }: NewProjectsModalProps) => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');

  if (!isOpen) return null;

  const projects = [
    {
      id: 1,
      key: 'CASE_UV_SMART_PHONE',
      title: 'CASE U/V - SMART PHONE',
      inventionTitle: 'UV Protection Case Technology',
      description: 'UV protection technology for smartphones with advanced safety features',
      icon: 'ri-smartphone-line',
      color: 'from-blue-500 to-cyan-500',
      status: 'Coming Soon',
      statusColor: 'bg-yellow-500',
    },
    {
      id: 2,
      key: 'MICROSENSE_AI_SKIN',
      title: 'MICROSENSE AI SKIN',
      inventionTitle: 'AI-Powered Skin Analysis System',
      description: 'AI-powered skin analysis and personalized skincare recommendations',
      icon: 'ri-microscope-line',
      color: 'from-purple-500 to-pink-500',
      status: 'In Development',
      statusColor: 'bg-orange-500',
    },
    {
      id: 3,
      key: 'STOPSCAM_AI',
      title: 'STOPSCAM AI',
      inventionTitle: 'Advanced Fraud Detection AI',
      description: 'Advanced fraud detection and prevention system for online security',
      icon: 'ri-shield-check-line',
      color: 'from-red-500 to-orange-500',
      status: 'Beta Testing',
      statusColor: 'bg-green-500',
    },
    {
      id: 4,
      key: 'SMARTPATCHAI',
      title: 'SMARTPATCHAI',
      inventionTitle: 'Intelligent Health Monitoring Patch',
      description: 'Intelligent health monitoring patches with real-time data tracking',
      icon: 'ri-heart-pulse-line',
      color: 'from-green-500 to-teal-500',
      status: 'Coming Soon',
      statusColor: 'bg-yellow-500',
    },
    {
      id: 5,
      key: 'VASCULARSCAN_AI',
      title: 'VASCULARSCAN AI',
      inventionTitle: 'AI Vascular Scanning Technology',
      description: 'AI-driven vascular scanning technology for medical diagnostics',
      icon: 'ri-stethoscope-line',
      color: 'from-indigo-500 to-purple-500',
      status: 'In Development',
      statusColor: 'bg-orange-500',
    },
    {
      id: 6,
      key: 'GOVSECURE_AI',
      title: 'GOVSECURE AI',
      inventionTitle: 'Government-Grade Security System',
      description: 'Government-grade security solutions powered by artificial intelligence',
      icon: 'ri-government-line',
      color: 'from-gray-600 to-gray-800',
      status: 'Planning',
      statusColor: 'bg-gray-500',
    },
    {
      id: 7,
      key: 'WORLDDOME195',
      title: 'WORLDDOME195',
      inventionTitle: 'Global Connectivity Platform',
      description: 'Global connectivity platform connecting 195 countries worldwide',
      icon: 'ri-global-line',
      color: 'from-cyan-500 to-blue-500',
      status: 'Coming Soon',
      statusColor: 'bg-yellow-500',
    },
    {
      id: 8,
      key: '12ZODIAC_FASHION',
      title: '12ZODIAC FASHION',
      inventionTitle: 'Astrology-Based Fashion AI',
      description: 'Personalized fashion recommendations based on zodiac signs and astrology',
      icon: 'ri-star-line',
      color: 'from-pink-500 to-rose-500',
      status: 'In Development',
      statusColor: 'bg-orange-500',
    },
  ];

  const handleProjectClick = async (project: any) => {
    // Nếu là CASE U/V, chuyển sang trang demo
    if (project.key === 'CASE_UV_SMART_PHONE') {
      onClose();
      navigate('/case-uv-demo');
      return;
    }

    // Các dự án khác vẫn tải video như cũ
    try {
      const videoData = await getProjectVideoByName(project.key);
      if (videoData && videoData.video_url) {
        setSelectedProject(project);
        setVideoUrl(videoData.video_url);
      } else {
        alert('Dự án này chưa có video. Vui lòng upload video trước!');
      }
    } catch (error) {
      console.error('Error loading video:', error);
      alert('Dự án này chưa có video. Vui lòng upload video trước!');
    }
  };

  const closeVideoModal = () => {
    setSelectedProject(null);
    setVideoUrl('');
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden border border-white/10">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-teal-500 to-cyan-500 p-8">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>
            <h2 className="text-3xl font-bold text-white mb-2">NEW PROJECTS</h2>
            <p className="text-white/90 text-lg">Discover our latest innovations and upcoming releases</p>
          </div>

          {/* Content */}
          <div className="p-8 overflow-y-auto max-h-[calc(90vh-180px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleProjectClick(project)}
                  className="group relative bg-gradient-to-br from-white/5 to-white/10 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`${project.statusColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                      {project.status}
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br ${project.color} mb-4 group-hover:scale-110 transition-transform`}>
                    <i className={`${project.icon} text-white text-3xl`}></i>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-2">
                    {project.title}
                  </h3>
                  
                  {/* Title of Invention */}
                  <div className="mb-3 pb-3 border-b border-white/10">
                    <p className="text-xs text-teal-400 font-semibold mb-1">TITLE OF INVENTION</p>
                    <p className="text-sm text-white/90 font-medium">
                      {project.inventionTitle}
                    </p>
                  </div>
                  
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {project.description}
                  </p>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500/0 to-cyan-500/0 group-hover:from-teal-500/10 group-hover:to-cyan-500/10 rounded-2xl transition-all duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedProject && videoUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fade-in">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={closeVideoModal}
          ></div>

          {/* Video Container */}
          <div className="relative bg-black rounded-2xl shadow-2xl max-w-4xl w-full border border-white/20 overflow-hidden">
            {/* Header */}
            <div className="relative bg-gradient-to-r from-teal-500 to-cyan-500 p-6">
              <button
                onClick={closeVideoModal}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-white text-xl"></i>
              </button>
              <h3 className="text-2xl font-bold text-white mb-1">{selectedProject.title}</h3>
              <p className="text-white/90">{selectedProject.inventionTitle}</p>
            </div>

            {/* Video Player */}
            <div className="relative bg-black aspect-video">
              <video
                src={videoUrl}
                controls
                autoPlay
                className="w-full h-full"
              >
                Trình duyệt của bạn không hỗ trợ video.
              </video>
            </div>

            {/* Description */}
            <div className="p-6 bg-gray-900">
              <p className="text-gray-300 leading-relaxed">
                {selectedProject.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewProjectsModal;
